import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { DirectionsBike, Email, Lock, Visibility, VisibilityOff, PedalBike } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { validateEmail } from '../utils/validation';

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setFieldErrors(prev => ({ ...prev, email: '请输入有效的邮箱地址' }));
    } else {
      setFieldErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePasswordBlur = () => {
    if (formData.password && formData.password.length < 6) {
      setFieldErrors(prev => ({ ...prev, password: '密码至少需要6个字符' }));
    } else {
      setFieldErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // 前端验证
    if (!formData.email || !formData.password) {
      setError('请填写所有字段');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('请输入有效的邮箱地址');
      return;
    }
    
    setLoading(true);
    try { 
      await login(formData.email, formData.password); 
      navigate('/dashboard'); 
    }
    catch (err) { 
      setError(err.response?.data?.message || '登录失败'); 
    }
    finally { 
      setLoading(false); 
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      bgcolor: '#f8f9fa',
      backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden',
      p: 2
    }}>
      {/* 背景装饰 */}
      <Box sx={{
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%)',
        animation: 'pulse 15s ease-in-out infinite'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '-30%',
        right: '-30%',
        width: '60%',
        height: '60%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
        borderRadius: '50%'
      }} />

      <Card sx={{ 
        maxWidth: 420, 
        width: '100%', 
        borderRadius: 4,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        position: 'relative',
        overflow: 'visible',
        animation: 'slideUp 0.6s ease-out'
      }}>
        {/* 顶部装饰 */}
        <Box sx={{
          position: 'absolute',
          top: -40,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: '#fff',
          borderRadius: '50%',
          p: 1.5,
          boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
        }}>
          <DirectionsBike sx={{ fontSize: 50, color: 'primary.main' }} />
        </Box>
        
        <CardContent sx={{ p: 5, pt: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('appName')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              专业的自行车配置管理系统
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField 
              fullWidth 
              label={t('email')} 
              type="email"
              value={formData.email} 
              onChange={(e) => setFormData({...formData, email: e.target.value})} 
              onBlur={handleEmailBlur}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
            />
            
            <TextField 
              fullWidth 
              label={t('password')} 
              type={showPassword ? 'text' : 'password'}
              value={formData.password} 
              onChange={(e) => setFormData({...formData, password: e.target.value})} 
              onBlur={handlePasswordBlur}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label={showPassword ? '隐藏密码' : '显示密码'} onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button 
              fullWidth 
              type="submit" 
              variant="contained" 
              size="large" 
              disabled={loading} 
              sx={{ 
                mt: 3, 
                mb: 2,
                borderRadius: 2,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('login')}
            </Button>
          </form>
          
          <Typography variant="body2" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
            当前系统关闭自助注册，请使用管理员分发的统一账号登录
          </Typography>
        </CardContent>
      </Card>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </Box>
  );
}
